<!--
  app.component.html
  This is the main layout for the ReceivingMgr app, containing:
  - Sidebar Menu: Visible when the user is logged in with navigation options.
  - Header: Displays app title, current page title, and user-specific options like login/logout and location selector.
  - Dynamic Page Content: Rendered via Angular routing.
  - Footer: Displays app and assets version.
  - Template for app title/logo when the header is hidden.

  Includes Ionic components for responsive design, with specific layout changes based on screen size (small vs large).
  When in development mode, an offscreen 'app-load-all-ionic' component is loaded to ensure all Ionic components are included in the build.
-->

<ion-app>
  
  <!-- Menu: Sidebar that appears when the menu button is clicked.
       Suppressed on a standalone page (the public install-help route), which
       renders its own frame — see AppComponent.isStandalonePage. -->
  <ion-menu contentId="main-page" *ngIf="loggedIn && !isStandalonePage">

    <!-- App Title and Logo (Only visible on small screens) -->
    <div class="app-title-containter" *ngIf="isSmallScreen">
      <div class="app-menu-title-wrapper">
        <img src="assets/delivery-box.png" class="app-logo" alt="box">
        {{appName}}
      </div> 
    </div>
  

        <!-- Main Content of the Menu -->
    <ion-content>

 
  
   <!-- Menu Items: List of navigation links in the sidebar -->
      <ion-list>
       
        <!-- Receiving Section -->
        <ion-list-header>
          <ion-label color="secondary">Receiving</ion-label>
        </ion-list-header>

        <ion-menu-toggle auto-hide="false" *ngFor="let page of appPages.slice(0, 2); let i = index">
          <ion-item
            (click)="onMenuClick(page.menuTitle, i)"
            routerDirection="root"
            [routerLink]="[resolveMenuUrl(page)]"
            lines="none"
            detail="false"
            [class.selected]="selectedIndex === i">
            <!-- Menu icon and label with dynamic class for active selection -->
            <ion-icon [class]="selectedIndex === i?'selected-icon':''" slot="start" [name]="page.icon"></ion-icon>
            <ion-label>{{ page.menuTitle }}</ion-label>
          </ion-item>
        </ion-menu-toggle>

        <!-- SOF Receiving Section -->
        <ion-list-header>
          <ion-label color="secondary">SOF Receiving</ion-label>
        </ion-list-header>

        <ion-menu-toggle auto-hide="false" *ngFor="let page of appPages.slice(4, 6); let i = index">
          <ion-item
            (click)="onMenuClick(page.menuTitle, i + 4)"
            routerDirection="root"
            [routerLink]="[page.url]"
            lines="none"
            detail="false"
            [class.selected]="selectedIndex === i + 4">
            <!-- Menu icon and label with dynamic class for active selection -->
            <ion-icon [class]="selectedIndex === i + 4?'selected-icon':''" slot="start" [name]="page.icon"></ion-icon>
            <ion-label>{{ page.menuTitle }}</ion-label>
          </ion-item>
        </ion-menu-toggle>

        <!-- Laydown Space Section -->
        <ion-list-header>
          <ion-label color="secondary">Laydown Space</ion-label>
        </ion-list-header>

        <ion-menu-toggle auto-hide="false" *ngFor="let page of appPages.slice(2, 4); let i = index">
          <ion-item
            (click)="onMenuClick(page.menuTitle, i + 2)"
            routerDirection="root"
            [routerLink]="[page.url]"
            lines="none"
            detail="false"
            [class.selected]="selectedIndex === i + 2">
            <!-- Menu icon and label with dynamic class for active selection -->
            <ion-icon [class]="selectedIndex === i + 2?'selected-icon':''" slot="start" [name]="page.icon"></ion-icon>
            <ion-label>{{ page.menuTitle }}</ion-label>
          </ion-item>
        </ion-menu-toggle>

     
        

        <!-- Settings Section -->
        <ion-list-header>
          <ion-label color="secondary">Settings</ion-label>
        </ion-list-header>

        <!-- The print outage switch. Here rather than in the per-job print
             config, because "this printer is broken" is a fact about the
             building, not about this delivery — see PrintFallbackModalComponent. -->
        <ion-menu-toggle auto-hide="false">
          <ion-item
            button
            (click)="showPrinterStatus = true"
            lines="none"
            detail="false">
            <ion-icon slot="start" name="print-outline"></ion-icon>
            <ion-label>
              Print Settings
              <!-- The menu's own reminder that an outage override is on, for
                   anyone who opens it from a route where the banner has scrolled
                   away. It names what moved rather than badging a bare "On",
                   which said nothing: printing is always on, and the question is
                   only ever where it is going. -->
              <p *ngIf="printFallbackActive">{{ printFallbackSummary }}</p>
            </ion-label>
            <ion-badge slot="end" color="warning" *ngIf="printFallbackActive">Temporary</ion-badge>
          </ion-item>
        </ion-menu-toggle>

        <!-- Help Section -->
        <ion-list-header>
          <ion-label color="secondary">Help</ion-label>
        </ion-list-header>

        <!-- Opens the install instructions in a new tab, so whatever the user was
             in the middle of is still here when they come back. Worded for people
             who have never heard the term "PWA" — see openInstallHelp(). -->
        <ion-menu-toggle auto-hide="false">
          <ion-item
            button
            (click)="openInstallHelp()"
            lines="none"
            detail="false">
            <ion-icon slot="start" name="download-outline"></ion-icon>
            <ion-label>Install App on Your Phone</ion-label>
            <!-- Standard "leaves this page" hint, dimmed so it reads as a hint
                 rather than a second action. -->
            <ion-icon slot="end" name="open" class="menu-external-icon"></ion-icon>
          </ion-item>
        </ion-menu-toggle>

        <!-- Logout Section -->
        <ion-list-header>
          <ion-label color="secondary">Account</ion-label>
        </ion-list-header>

        <!-- Logout Button -->
        <ion-menu-toggle auto-hide="false">
          <ion-item
            class="logout-button"
            (click)="logout()" 
            lines="none" 
            detail="false">
            <ion-icon slot="start" name="log-out-outline"></ion-icon>
            <ion-label>Logout {{userName}}</ion-label>
          </ion-item>
        </ion-menu-toggle>

      </ion-list>
    
        <!-- Display app version and assets version at the bottom of the menu -->
      <div class="version-info">
        <div>
          <small>App Version: {{ appVersion }}</small>
        </div>
        <div>
          <small>Assets Version: {{ assetsVersion }}</small>
        </div>
      </div>
    </ion-content>
  
  </ion-menu>


  <!-- Main Page Content Area -->
<div class="ion-page" id="main-page">

  <!-- Offline notice: appears on every route when the device loses its connection
       and goes away when it returns. Above the header rather than fixed over it,
       so nothing it covers is something the user still needs to tap. Shown on the
       standalone page too — losing signal is a device fact, not shell chrome for
       a signed-in user. -->
  <app-offline-banner></app-offline-banner>

  <!-- Print-fallback notice: up on every route for as long as this device is
       routing printing around a dead printer, with the one control that ends it.
       Below the offline banner because a lost connection is the more urgent of
       the two, and because printing is moot while offline anyway. Shown on the
       standalone page too — it is a device fact, like connectivity. -->
  <app-print-fallback-banner></app-print-fallback-banner>

    <!-- Header Banner: Contains the app logo and title, with dynamic menu buttons.
         Hidden on a standalone page (the public install-help route): everything in
         it — menu button, location selector, login state — is shell chrome for a
         signed-in user, and that page supplies its own header instead. -->
  <ion-header *ngIf="!isStandalonePage">
    <ion-toolbar>
      <div class="header-container">
                 <!-- Left Section: Menu button to toggle the sidebar -->
        <div class="header-section header-start">
          <ion-buttons slot="start">
            <!-- color="light": the red toolbar has no explicit content color, so the
                 button would otherwise inherit the default dark color and be invisible
                 on red (the title/login button hardcode white for the same reason).
                 icon="menu-outline": pin to the registered glyph so md mode (Android),
                 which defaults to the unregistered 'menu', still renders it. -->
            <ion-menu-button color="light" icon="menu-outline"></ion-menu-button>
          </ion-buttons>
          <div class="app-title-wrapper">
            <img src="assets/delivery-box.png" class="app-logo" alt="box"> 
         <span *ngIf="!isSmallScreen">  ReceivingMgr </span>
          <app-select-location 
            *ngIf="isSmallScreen && loggedIn"
            [locationsRecord]="locationsRecord"
            (locationChange)="onLocationChange($event)"
            [showFieldLabel]="false"
            [selectedLocationName]="selectedLocationName">
          </app-select-location>
          </div>
      
        </div>

               <!-- Center Section: Title of the current page (desktop only —
                    on mobile the header has no room for it and it duplicates the
                    location shown at the start) -->
        <div class="header-section header-center" *ngIf="!isSmallScreen">
          <ion-title>
            {{pageTitle}}<!-- Dynamic page title -->
          </ion-title>
        </div>

          <!-- Right Section: Location selector and login/logout buttons -->
        <div class="header-section header-end">
          <app-select-location 
            *ngIf="!isSmallScreen && loggedIn"
            [locationsRecord]="locationsRecord"
            (locationChange)="onLocationChange($event)"
            [selectedLocationName]="selectedLocationName">
          </app-select-location>

                <!-- Login button (Visible when the user is logged out) -->
          <ion-buttons slot="end" *ngIf="!loggedIn">
            <ion-button (click)="login()" color="light">
     <ion-icon slot="start" name="log-in-outline"></ion-icon>
              Login
            </ion-button>
          </ion-buttons>

            <!-- Company logo shown on the right section -->
          <ion-buttons slot="end">
            <img [src]="'assets/logo.png?v=' + timestamp" alt="Logo" style="height: 40px; width: auto;">
          </ion-buttons>
        </div>
      </div>
    </ion-toolbar>
  </ion-header>


    <!-- Page content (Rendered dynamically using Angular routing) -->
<div  id="main-page-content"
>
  <router-outlet></router-outlet> <!-- Dynamic content based on route -->
</div>


</div>


  <!-- Template for displaying the app name and logo (used when header is hidden) -->
<ng-template #headerTemplate>
    <div class="app-title-wrapper">
      <img src="assets/delivery-box.png"  class="app-logo" alt="box"  >
      {{appName}} <!-- App name displayed in the header template -->
    </div>
</ng-template>

     


  <!-- Install instructions, opened from the "add to your home screen" toast
       (see maybeOfferInstall). Hosted here rather than on a page because the
       toast is app-level; it renders the same shared content the public
       /install-help route serves. -->
  <app-pwa-instructions-modal
    [isOpen]="showInstallHelp"
    (closeModal)="showInstallHelp = false">
  </app-pwa-instructions-modal>

  <!-- The printer-status switch, opened from the menu's Settings section. Hosted
       here because the mode is app-level: it is read by the mobile flow, the
       desktop form and the log table's reprint alike. -->
  <app-print-fallback-modal
    [isOpen]="showPrinterStatus"
    (closeModal)="showPrinterStatus = false">
  </app-print-fallback-modal>

  <!-- Offscreen component for loading additional Ionic components in dev mode -->
 <app-load-all-ionic *ngIf="isDevMode"> </app-load-all-ionic>

</ion-app>